import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useRouteChange } from '../../../contexts/RouteChangeContext';

/**
 * Automatically detects when a page is ready and calls pageReady
 * This uses various heuristics to determine if content is likely loaded:
 * 1. DOM content loaded event
 * 2. Main content mutation detection
 * 3. Timeout based fallback
 */
const PageReadyHandler = ({ contentSelector = 'main', delay = 100 }) => {
  const { pageReady } = useRouteChange();
  const location = useLocation();
  const observerRef = useRef(null);
  const timerRef = useRef(null);
  
  useEffect(() => {
    // Function to mark the page as ready
    const markPageAsReady = () => {
      pageReady();
      
      // Clean up
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };

    // Wait a short delay before checking DOM (gives React time to render)
    timerRef.current = setTimeout(() => {
      const mainContent = document.querySelector(contentSelector);
      
      if (mainContent) {
        // Create mutation observer to detect content changes
        observerRef.current = new MutationObserver((mutations) => {
          // If we detect significant mutations, content is likely loaded
          if (mutations.length > 0) {
            // Wait a small delay to let any final renders complete
            setTimeout(markPageAsReady, 30);
            observerRef.current.disconnect();
          }
        });
        
        // Start observing
        observerRef.current.observe(mainContent, { 
          childList: true, 
          subtree: true,
          attributes: false,
          characterData: false
        });
        
        // Fallback timeout - if no DOM changes in 300ms, assume page is ready
        setTimeout(markPageAsReady, 300);
      } else {
        // If no main content element, just mark ready after delay
        markPageAsReady();
      }
    }, delay);
    
    // Clean up on location change or unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [location.pathname, pageReady, contentSelector, delay]);

  // This component doesn't render anything
  return null;
};

PageReadyHandler.propTypes = {
  contentSelector: PropTypes.string,
  delay: PropTypes.number
};

export default PageReadyHandler;

